/**
 * BigActionButton — Non-HR UX layer (HUMANAI_UX_NONHR.md §3, กลุ่ม Workforce).
 *
 * "เร็วสุด ไม่ต้องคิด" — ปุ่มงานหลักจอเดียวสำหรับพนักงานทั่วไปบน LINE/LIFF.
 * เปิดแอปมา → เห็นสิ่งที่ต้องทำตอนนี้เป็นปุ่มใหญ่ → แตะครั้งเดียว. ไม่ต้อง
 * navigate หาเมนู. รองรับสถานะ "เสร็จแล้ว" (done) กันกดซ้ำ.
 *
 * variant:
 *   cta      — teal ปกติ (default)
 *   gradient — brand-gradient · ใช้กับ "signature moment" เท่านั้น
 *              (เช่น ปุ่มเช็คอิน — ดู design system §4: gradient = signature)
 *
 * Portable — CSS class กลาง + lucide เท่านั้น (reuse ข้าม product).
 */
import type { ComponentType } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

export function BigActionButton({
  icon: Icon,
  label,
  sublabel,
  to,
  onClick,
  done = false,
  doneLabel = 'เสร็จแล้ววันนี้',
  variant = 'cta',
  disabled = false,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  sublabel?: string;
  to?: string;
  onClick?: () => void;
  /** แสดงสถานะเสร็จแล้ว (ไม่กดได้) */
  done?: boolean;
  doneLabel?: string;
  variant?: 'cta' | 'gradient';
  disabled?: boolean;
}) {
  if (done) {
    return (
      <div className="card flex items-center gap-3 px-5 py-4 border-l-4 border-l-brand-500">
        <div className="w-11 h-11 rounded-full bg-brand-50 flex items-center justify-center shrink-0">
          <Check className="w-6 h-6 text-brand-600" />
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-gray-900">{doneLabel}</p>
          {sublabel && <p className="text-sm text-gray-500 truncate">{sublabel}</p>}
        </div>
      </div>
    );
  }

  const base =
    'w-full flex items-center gap-4 px-5 py-4 rounded-xl shadow-sm transition active:scale-[0.98] disabled:opacity-50';
  const skin = variant === 'gradient' ? 'bg-brand-gradient text-white' : 'bg-teal text-white hover:bg-teal-dark';

  const inner = (
    <>
      <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${variant === 'gradient' ? 'bg-white/20' : 'bg-white/15'}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="text-left min-w-0 flex-1">
        <p className="font-display text-lg font-semibold leading-tight">{label}</p>
        {sublabel && <p className="text-sm opacity-90 truncate">{sublabel}</p>}
      </div>
    </>
  );

  if (to && !disabled) {
    return (
      <Link to={to} className={`${base} ${skin}`}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={`${base} ${skin}`}>
      {inner}
    </button>
  );
}
